import { BulkDeviceIdsRequest, Device } from '@/api';
import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { UIToast } from '@/ui';
import { useDevicesTranslation } from '../hooks';
import { addOrUpdateAllDevicesCache } from '../queries/devices-cache';

type UseBulkPowerOffDevicesActionProps = {
    companyId: string;
};

/**
 * Everything the caller chooses per call. The company is not among them: this
 * hook is scoped to the company whose device list the selection was made in,
 * and every selected device has to belong to it.
 */
export type BulkPowerOffDevicesVariables = Omit<
    BulkDeviceIdsRequest,
    'companyId'
>;

type UseBulkPowerOffDevicesActionData = {
    bulkPowerOffDevices: (
        variables: BulkPowerOffDevicesVariables
    ) => Promise<Device[]>;
    loading: boolean;
    error: string;
};

/**
 * Queueing a power off on every selected device. Each one has to be
 * switched back on by hand afterwards.
 *
 * One request for the whole selection, applied all-or-nothing: either every
 * device is changed or none is, which is why a failure leaves nothing to
 * report device by device.
 *
 * No `isAllowed`/`reason`: the bulk bar only appears for a viewer who may edit
 * the devices they have selected, and whether a *feature* can be turned on
 * across them is answered by `useBulkAlertLicenses`.
 */
export function useBulkPowerOffDevicesAction(
    props: UseBulkPowerOffDevicesActionProps
): UseBulkPowerOffDevicesActionData {
    const { companyId } = props;

    const { t } = useDevicesTranslation();
    // The client is swapped out on token refresh, so never capture it: read
    // `openApiRef.current` at call time or you keep using a dead token.
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    // One place that turns a failure into a message. Every error
    // `POST /v3/devices/bulk/actions/power-off` declares gets a branch.
    // `COMPANY_NOT_FOUND` is the exception: this namespace has nothing of its
    // own to say about it, so it falls through to the generic message.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            if (isApiError(error, 'DEVICE_NOT_FOUND')) {
                return t('shared.errors.device_not_found');
            }
            if (isApiError(error, 'DEVICE_NOT_SETUP')) {
                return t('shared.errors.device_not_setup');
            }
            if (isApiError(error, 'NO_PERMISSION')) {
                return t('shared.errors.unauthorised');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                // The server names the offending field; it is more use than
                // anything generic we could write.
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: (variables: BulkPowerOffDevicesVariables) =>
            openApiRef.current.devices
                .bulkPowerOffDevices({ ...variables, companyId })
                .then((response) => response.data.data.devices),
        onSuccess: (devices) => {
            devices.forEach((device) => {
                addOrUpdateAllDevicesCache(queryClient, device);
            });

            UIToast.success(
                t('actions.bulk_power_off_devices.success', {
                    count: devices.length
                })
            );
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        bulkPowerOffDevices: (variables) => mutation.mutateAsync(variables),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
